import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Building2,
  ChevronDown,
  ChevronRight,
  Edit2,
  Eye,
  Home,
  Layers,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import type {
  BuildingForm,
  FloorForm,
  IBuilding,
  IFloor,
} from "../types/quarters.types";

// ─── Constants ────────────────────────────────────────────────────────────────

const QUARTER_TYPES = ["Type I", "Type II", "Type III", "Type IV", "Type V", "Guest House", "VIP Suite"];

const EMPTY_BUILDING: BuildingForm = {
  name: "",
  code: "",
  campus: "",
  address: "",
  description: "",
  buildingType: "",
  buildingCategory: "",
  quarterType: "Type II",
  status: "active",
};

/** quarterType is intentionally absent — it is a building-level field, not a floor field. */
const EMPTY_FLOOR: FloorForm = {
  name: "",
  floorNumber: 1,
  numberOfFlats: 0,
  description: "",
};

// ─── Component ────────────────────────────────────────────────────────────────

const BuildingManagement = () => {
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  const qs = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const [buildings, setBuildings] = useState<IBuilding[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [floorsMap, setFloorsMap] = useState<Record<string, IFloor[]>>({});
  const [flatCountMap, setFlatCountMap] = useState<Record<string, number>>({});

  const [buildingModal, setBuildingModal] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState<IBuilding | null>(null);
  const [bForm, setBForm] = useState<BuildingForm>(EMPTY_BUILDING);
  const [savingBuilding, setSavingBuilding] = useState(false);

  const [floorModal, setFloorModal] = useState(false);
  const [targetBuilding, setTargetBuilding] = useState<IBuilding | null>(null);
  const [editingFloor, setEditingFloor] = useState<IFloor | null>(null);
  const [fForm, setFForm] = useState<FloorForm>(EMPTY_FLOOR);
  const [savingFloor, setSavingFloor] = useState(false);

  const loadBuildings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await qs.getBuildings();
      if (res.success) setBuildings(res.data || []);
    } catch (err: any) {
      toast.error(err.message || "Unable to load buildings. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [qs, toast]);

  /**
   * Fetch floors for a building.
   * GET /buildings/:id returns { data: { building, floors, stats } }
   */
  const refreshBuildingFloors = useCallback(async (buildingId: string) => {
    const res = await qs.getBuilding(buildingId);
    if (!res.success) return;

    // The API response nests data: { building, floors, stats }
    const floors: IFloor[] = res.data.floors || [];
    setFloorsMap((prev) => ({ ...prev, [buildingId]: floors }));

    const counts = await Promise.all(
      floors.map(async (floor) => {
        try {
          const flatRes = await qs.getFloorFlats(floor._id);
          return [floor._id, flatRes.success ? flatRes.count : floor.numberOfFlats ?? 0] as const;
        } catch {
          return [floor._id, floor.numberOfFlats ?? 0] as const;
        }
      })
    );

    setFlatCountMap((prev) => ({ ...prev, ...Object.fromEntries(counts) }));
  }, [qs]);

  useEffect(() => {
    loadBuildings();
  }, [loadBuildings]);

  const toggleExpand = async (building: IBuilding) => {
    if (expandedId === building._id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(building._id);
    if (!floorsMap[building._id]) {
      try {
        await refreshBuildingFloors(building._id);
      } catch (err: any) {
        toast.error(err.message || "Unable to load floors for this building.");
      }
    }
  };

  const openBuildingModal = (building: IBuilding | null = null) => {
    setEditingBuilding(building);
    setBForm(
      building
        ? {
            name: building.name || "",
            code: building.code || "",
            campus: building.campus || "",
            address: building.address || "",
            description: building.description || "",
            buildingType: building.buildingType || "",
            buildingCategory: building.buildingCategory || "",
            quarterType: building.quarterType || "Type II",
            status: building.status || "active",
          }
        : EMPTY_BUILDING
    );
    setBuildingModal(true);
  };

  const saveBuildingForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingBuilding(true);
    try {
      const res = editingBuilding
        ? await qs.updateBuilding(editingBuilding._id, bForm)
        : await qs.createBuilding(bForm);

      if (res.success) {
        toast.success(editingBuilding ? "Building updated successfully." : "Building created successfully.");
        setBuildingModal(false);
        setEditingBuilding(null);
        await loadBuildings();
      }
    } catch (err: any) {
      toast.error(err.message || "Unable to save building. Check required fields and try again.");
    } finally {
      setSavingBuilding(false);
    }
  };

  const deleteBuilding = async (building: IBuilding) => {
    const label = building?.name || "this building";
    if (!window.confirm(`Delete ${label}? Vacant floors and flats inside it will be removed from active inventory.`)) return;

    try {
      const res = await qs.deleteBuilding(building._id);
      if (res.success) {
        toast.success(res.message || "Building deleted successfully.");
        setExpandedId((prev) => (prev === building._id ? null : prev));
        await loadBuildings();
      }
    } catch (err: any) {
      toast.error(err.message || "Unable to delete building. Vacate or release linked flats first.");
    }
  };

  const openFloorModal = (building: IBuilding, floor: IFloor | null = null) => {
    const existingFloors = floorsMap[building._id] || [];
    setTargetBuilding(building);
    setEditingFloor(floor);
    setFForm(
      floor
        ? {
            name: floor.name || "",
            floorNumber: floor.floorNumber ?? 0,
            numberOfFlats: flatCountMap[floor._id] ?? floor.numberOfFlats ?? 0,
            description: floor.description || "",
          }
        : {
            ...EMPTY_FLOOR,
            floorNumber: existingFloors.length + 1,
          }
    );
    setFloorModal(true);
  };

  const saveFloorForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetBuilding) return;

    setSavingFloor(true);
    try {
      const payload = editingFloor
        ? { ...fForm, sequence: fForm.floorNumber }
        : fForm;

      const res = editingFloor
        ? await qs.updateFloor(editingFloor._id, payload)
        : await qs.createFloor(targetBuilding._id, payload);

      if (res.success) {
        const createdFlats = res.data?.flats?.length || 0;
        toast.success(
          editingFloor
            ? "Floor updated successfully."
            : createdFlats > 0
            ? `Floor added and ${createdFlats} flat(s) were created automatically.`
            : "Floor added successfully."
        );
        setFloorModal(false);
        setEditingFloor(null);
        await refreshBuildingFloors(targetBuilding._id);
        await loadBuildings();
      }
    } catch (err: any) {
      toast.error(err.message || `Unable to ${editingFloor ? "update" : "add"} floor. Please check the details.`);
    } finally {
      setSavingFloor(false);
    }
  };

  const deleteFloor = async (floor: IFloor, buildingId: string) => {
    if (!window.confirm(`Delete ${floor.name || "this floor"}? Vacant flats on this floor will be removed from active inventory.`)) return;

    try {
      const res = await qs.deleteFloor(floor._id);
      if (res.success) {
        toast.success(res.message || "Floor deleted successfully.");
        await refreshBuildingFloors(buildingId);
        await loadBuildings();
      }
    } catch (err: any) {
      toast.error(err.message || "Unable to delete floor. Vacate or release linked flats first.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <button
              onClick={() => navigate("/quarters")}
              className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-sm font-medium mb-3 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to dashboard
            </button>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-3">
              <Building2 className="w-7 h-7 text-blue-600" />
              Building & Floor Management
            </h1>
            <p className="text-slate-500 text-sm mt-1">Configure buildings, floors, and flat inventory structure.</p>
          </div>
          <button
            onClick={() => openBuildingModal()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-lg border border-slate-800 shadow-sm font-medium hover:bg-slate-800 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Building
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 bg-white border border-slate-200 rounded-xl shadow-sm">
            <div className="animate-spin rounded-full h-9 w-9 border-4 border-blue-600 border-t-transparent" />
          </div>
        ) : buildings.length === 0 ? (
          <div className="text-center py-20 bg-white border border-slate-200 rounded-xl shadow-sm">
            <Building2 className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900">No buildings yet</h2>
            <p className="text-slate-500 text-sm mt-1">Create a building before adding floors or flats.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {buildings.map((building) => {
              const isOpen = expandedId === building._id;
              const floors = floorsMap[building._id] || [];

              return (
                <div key={building._id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-5 py-4">
                    <button
                      className="flex items-start gap-3 flex-1 text-left min-w-0"
                      onClick={() => toggleExpand(building)}
                    >
                      {isOpen ? (
                        <ChevronDown className="w-5 h-5 text-slate-700 mt-1 shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400 mt-1 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{building.name}</span>
                          <span className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded uppercase font-semibold">
                            {building.code}
                          </span>
                          <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold">
                            {building.quarterType}
                          </span>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold uppercase ${
                            building.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"
                          }`}>
                            {building.status}
                          </span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1 truncate">
                          {building.campus && `${building.campus} - `}
                          {building.totalFloors || 0} floor{building.totalFloors === 1 ? "" : "s"} -{" "}
                          {building.totalFlats || 0} flat{building.totalFlats === 1 ? "" : "s"}
                          {building.buildingType ? ` - ${building.buildingType}` : ""}
                        </p>
                      </div>
                    </button>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => {
                          setExpandedId(building._id);
                          if (!floorsMap[building._id]) refreshBuildingFloors(building._id);
                          openFloorModal(building);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                      >
                        <Layers className="w-3.5 h-3.5" />
                        Add Floor
                      </button>
                      <button
                        onClick={() => openBuildingModal(building)}
                        className="p-2 text-slate-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit building"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteBuilding(building)}
                        className="p-2 text-slate-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete building"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t border-slate-100 bg-slate-50 px-5 py-4">
                      {floors.length === 0 ? (
                        <div className="text-center py-8 border border-dashed border-slate-200 rounded-xl bg-white">
                          <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500 text-sm">No floors configured for this building.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {floors.map((floor) => {
                            const flatCount = flatCountMap[floor._id] ?? floor.numberOfFlats ?? 0;
                            return (
                              <div
                                key={floor._id}
                                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                                    <span className="text-slate-700 font-semibold text-sm">{floor.floorNumber}</span>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-slate-800 text-sm">{floor.name}</p>
                                    <p className="text-slate-500 text-xs">
                                      {flatCount} flat{flatCount === 1 ? "" : "s"}
                                      {floor.description ? ` - ${floor.description}` : ""}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => navigate(`/quarters/admin/flats?floorId=${floor._id}&buildingId=${building._id}`)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                    Flats
                                  </button>
                                  <button
                                    onClick={() => openFloorModal(building, floor)}
                                    className="p-1.5 text-slate-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Edit floor"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteFloor(floor, building._id)}
                                    className="p-1.5 text-slate-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete floor"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Create / Edit Building Modal ── */}
      {buildingModal && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">{editingBuilding ? "Edit Building" : "Create Building"}</h3>
              <button onClick={() => setBuildingModal(false)} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={saveBuildingForm} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Building Name *">
                  <input required value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} className={inputClass} placeholder="Staff Block A" />
                </Field>
                <Field label="Code *">
                  <input required value={bForm.code} onChange={(e) => setBForm({ ...bForm, code: e.target.value.toUpperCase() })} className={`${inputClass} uppercase`} placeholder="SBA" />
                </Field>
                <Field label="Quarter Type *">
                  {editingBuilding ? (
                    <div>
                      <input
                        readOnly
                        value={bForm.quarterType}
                        className={`${inputClass} bg-slate-100 cursor-not-allowed`}
                        title="Quarter type cannot be changed once flats exist in this building."
                      />
                      <p className="text-slate-400 text-[10px] mt-1">Cannot be changed if flats exist.</p>
                    </div>
                  ) : (
                    <select required value={bForm.quarterType} onChange={(e) => setBForm({ ...bForm, quarterType: e.target.value })} className={inputClass}>
                      {QUARTER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  )}
                </Field>
                <Field label="Status">
                  <select value={bForm.status} onChange={(e) => setBForm({ ...bForm, status: e.target.value as "active" | "inactive" })} className={inputClass}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </Field>
                <Field label="Campus">
                  <input value={bForm.campus} onChange={(e) => setBForm({ ...bForm, campus: e.target.value })} className={inputClass} placeholder="Main Campus" />
                </Field>
                <Field label="Building Type">
                  <input value={bForm.buildingType} onChange={(e) => setBForm({ ...bForm, buildingType: e.target.value })} className={inputClass} placeholder="Residential" />
                </Field>
                <Field label="Category">
                  <input value={bForm.buildingCategory} onChange={(e) => setBForm({ ...bForm, buildingCategory: e.target.value })} className={inputClass} placeholder="Staff / Officer" />
                </Field>
              </div>
              <Field label="Address">
                <textarea value={bForm.address} onChange={(e) => setBForm({ ...bForm, address: e.target.value })} rows={2} className={inputClass} placeholder="Building address" />
              </Field>
              <Field label="Description">
                <textarea value={bForm.description} onChange={(e) => setBForm({ ...bForm, description: e.target.value })} rows={2} className={inputClass} placeholder="Optional notes" />
              </Field>
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setBuildingModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Cancel
                </button>
                <button type="submit" disabled={savingBuilding} className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-60">
                  {savingBuilding ? "Saving..." : editingBuilding ? "Update Building" : "Create Building"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Create / Edit Floor Modal ── */}
      {floorModal && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-b border-slate-200">
              <div>
                <h3 className="font-semibold text-slate-900">{editingFloor ? "Edit Floor" : "Add Floor"}</h3>
                <p className="text-slate-500 text-xs mt-0.5">
                  {targetBuilding?.name} — Flats inherit quarter type: <strong>{targetBuilding?.quarterType}</strong>
                </p>
              </div>
              <button onClick={() => setFloorModal(false)} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={saveFloorForm} className="p-6 space-y-4">
              <Field label="Floor Name *">
                <input required value={fForm.name} onChange={(e) => setFForm({ ...fForm, name: e.target.value })} className={inputClass} placeholder="Ground Floor" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Floor Number *">
                  <input type="number" required min={0} value={fForm.floorNumber} onChange={(e) => setFForm({ ...fForm, floorNumber: parseInt(e.target.value, 10) || 0 })} className={inputClass} />
                </Field>
                <Field label={editingFloor ? "Target Flat Count" : "No. of Flats (auto-create)"}>
                  <input type="number" min={0} max={100} value={fForm.numberOfFlats} onChange={(e) => setFForm({ ...fForm, numberOfFlats: parseInt(e.target.value, 10) || 0 })} className={inputClass} />
                </Field>
              </div>
              <Field label="Description">
                <textarea value={fForm.description} onChange={(e) => setFForm({ ...fForm, description: e.target.value })} rows={2} className={inputClass} placeholder="Optional floor notes" />
              </Field>
              {fForm.numberOfFlats > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-2.5">
                  <Home className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-blue-800 text-xs font-medium">
                    {editingFloor
                      ? "Changing the count can add vacant flats or remove highest-numbered non-occupied flats. Occupied/reserved flats are protected."
                      : `${fForm.numberOfFlats} flat${fForm.numberOfFlats === 1 ? "" : "s"} will be created as ${targetBuilding?.quarterType || "the building's quarter type"} with vacant status.`}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setFloorModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Cancel
                </button>
                <button type="submit" disabled={savingFloor} className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-60">
                  {editingFloor && <Save className="w-4 h-4" />}
                  {savingFloor ? "Saving..." : editingFloor ? "Update Floor" : "Add Floor"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const inputClass = "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent";

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="block space-y-1">
    <span className="text-xs font-semibold text-slate-600 uppercase">{label}</span>
    {children}
  </label>
);

export default BuildingManagement;
